/**
 * Colours Diana chooses: one per client group, one per client, one for the
 * hours she does not work.
 *
 * She picks them, so nothing here may assume a palette. A block filled with
 * her colour still has to be readable, which is why `readableTextOn` exists
 * — black text on navy is unreadable, and so is white text on pale yellow.
 * That decision is arithmetic, not taste, so it lives here and is tested.
 */
const HEX_LONG = /^#?([0-9a-f]{6})$/i;
const HEX_SHORT = /^#?([0-9a-f]{3})$/i;
/** Anything a colour input or a paste might produce -> '#rrggbb', or null. */
export function normalizeHex(input) {
    const value = input.trim();
    const long = HEX_LONG.exec(value);
    if (long)
        return `#${long[1].toLowerCase()}`;
    const short = HEX_SHORT.exec(value);
    if (short) {
        const [r, g, b] = short[1].toLowerCase();
        return `#${r}${r}${g}${g}${b}${b}`;
    }
    return null;
}
export function isHexColor(input) {
    return normalizeHex(input) !== null;
}
function channels(hex) {
    const normalized = normalizeHex(hex) ?? '#000000';
    return [
        Number.parseInt(normalized.slice(1, 3), 16),
        Number.parseInt(normalized.slice(3, 5), 16),
        Number.parseInt(normalized.slice(5, 7), 16),
    ];
}
/** WCAG relative luminance, 0 (black) to 1 (white). */
export function luminance(hex) {
    const [r, g, b] = channels(hex).map((c) => {
        const v = c / 255;
        return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/** Deep navy rather than pure black — it is the ink the rest of the site uses. */
export const INK = '#1e2a35';
export const PAPER = '#ffffff';
/** WCAG contrast ratio between two colours, 1 (identical) to 21 (black on white). */
export function contrastRatio(a, b) {
    const la = luminance(a);
    const lb = luminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
/**
 * Text colour that stays legible on `hex`.
 *
 * Whichever of ink and white actually contrasts better, rather than a
 * luminance threshold. A threshold gets mid-tones wrong in a way that is
 * easy to miss: the gold in this palette sits just dark enough to look like
 * it wants white text, but white on it is 2.8:1 while ink on it is 5.5:1.
 */
export function readableTextOn(hex) {
    return contrastRatio(hex, INK) >= contrastRatio(hex, PAPER) ? INK : PAPER;
}
/** `#rrggbb` -> `rgba(r, g, b, a)`, for tinted backgrounds and soft borders. */
export function withAlpha(hex, alpha) {
    const [r, g, b] = channels(hex);
    const a = Math.min(1, Math.max(0, alpha));
    return `rgba(${r}, ${g}, ${b}, ${a})`;
}
/**
 * The swatches offered in the picker. Any hex is allowed — this is only the
 * quick choice, chosen to sit alongside gold and baby blue without shouting.
 *
 * Every one of them carries either ink or white text at 4.5:1 or better;
 * the test enforces it, which is how the first green and olive here were
 * caught and darkened.
 */
export const COLOR_PALETTE = [
    { hex: '#1e2a35', name: 'Grafit' },
    { hex: '#b8964f', name: 'Złoty' },
    { hex: '#6f9dc0', name: 'Błękit' },
    { hex: '#c0392b', name: 'Czerwony' },
    { hex: '#c98f7e', name: 'Łosoś' },
    { hex: '#7d5ba6', name: 'Fiolet' },
    { hex: '#26735f', name: 'Zieleń' },
    { hex: '#d98f2b', name: 'Pomarańcz' },
    { hex: '#4a6fa5', name: 'Granat' },
    { hex: '#6b7059', name: 'Oliwka' },
    { hex: '#a0567f', name: 'Wrzos' },
    { hex: '#5c5c5c', name: 'Szary' },
];
/** Hours she does not work, until she picks something else. */
export const DEFAULT_TIME_OFF_COLOR = '#1e2a35';
/** A lesson whose student has no colour and no coloured tag. Matches the gold trim. */
export const DEFAULT_LESSON_COLOR = '#b8964f';
export function tagColorMap(styles) {
    const map = new Map();
    for (const style of styles) {
        const hex = normalizeHex(style.color);
        if (hex)
            map.set(style.tag, hex);
    }
    return map;
}
/**
 * Which of a student's tags paints their lessons: the first one that has a
 * colour. A tile has a single background, so something has to win, and going
 * by her own tag order means she controls it by reordering rather than by
 * learning another concept.
 *
 * Returns the tag NAME, because the calendar labels the block and builds its
 * legend from it — not just the colour.
 */
export function coloringTag(client, tagColors) {
    if (!tagColors)
        return null;
    for (const tag of client?.tags ?? []) {
        if (tagColors.has(tag))
            return tag;
    }
    return null;
}
/**
 * The colour a lesson is drawn in: the student's own choice wins, then their
 * first coloured tag, then the default. One rule, used by every screen that
 * paints one.
 */
export function lessonColor(client, tagColors) {
    const own = client?.color ? normalizeHex(client.color) : null;
    if (own)
        return own;
    const tag = coloringTag(client, tagColors);
    const inherited = tag ? normalizeHex(tagColors.get(tag) ?? '') : null;
    return inherited ?? DEFAULT_LESSON_COLOR;
}
